import { useState } from "react";

const NATURE_STYLES = {
  remblai: { label:"Remblai" }, argile: { label:"Argile" }, limon: { label:"Limon" },
  sable: { label:"Sable" }, gravier: { label:"Gravier" }, marne: { label:"Marne" },
  roche: { label:"Roche" }, default: { label:"Sol" }
};

function CoupeGeologique({ couches, nappe, nappe_profondeur }) {
  const SVG_W = 280, SVG_H = 480, MARGE_G = 55, MARGE_D = 20, MARGE_H = 35, PXPM = 38;
  const profMax = Math.max(...couches.map(c => c.profondeur_base), 10);
  const echelle = (SVG_H - MARGE_H - 20) / profMax;

  const y = (prof) => MARGE_H + prof * echelle;
  const h = (c) => Math.max((c.profondeur_base - c.profondeur_toit) * echelle, 4);

  const patterns = `
    <defs>
      <pattern id="p_veg" patternUnits="userSpaceOnUse" width="8" height="8"><rect width="8" height="8" fill="#6B4423"/><circle cx="4" cy="4" r="1.5" fill="#3d2010"/></pattern>
      <pattern id="p_remblai" patternUnits="userSpaceOnUse" width="12" height="8"><rect width="12" height="8" fill="#C8A84B"/><line x1="0" y1="4" x2="12" y2="4" stroke="#a08030" stroke-width="1"/></pattern>
      <pattern id="p_argile" patternUnits="userSpaceOnUse" width="10" height="6"><rect width="10" height="6" fill="#7BA3C8"/><line x1="0" y1="3" x2="10" y2="3" stroke="#5a82a8" stroke-width="1"/></pattern>
      <pattern id="p_limon" patternUnits="userSpaceOnUse" width="10" height="8"><rect width="10" height="8" fill="#9BB8A0"/><line x1="0" y1="4" x2="10" y2="4" stroke="#708a75" stroke-width="1"/><circle cx="5" cy="2" r="1" fill="#708a75"/></pattern>
      <pattern id="p_sable" patternUnits="userSpaceOnUse" width="8" height="8"><rect width="8" height="8" fill="#F0D080"/><circle cx="2" cy="2" r="1" fill="#c8a840"/><circle cx="6" cy="5" r="1" fill="#c8a840"/><circle cx="4" cy="7" r="0.8" fill="#d0b050"/></pattern>
      <pattern id="p_gravier" patternUnits="userSpaceOnUse" width="12" height="10"><rect width="12" height="10" fill="#D4B860"/><ellipse cx="3" cy="5" rx="2.5" ry="2" fill="#a89040" stroke="#907030" stroke-width="0.5"/><ellipse cx="9" cy="3" rx="2" ry="1.5" fill="#b8a050" stroke="#907030" stroke-width="0.5"/><ellipse cx="7" cy="8" rx="2" ry="1.5" fill="#a89040" stroke="#907030" stroke-width="0.5"/></pattern>
      <pattern id="p_roche" patternUnits="userSpaceOnUse" width="14" height="10"><rect width="14" height="10" fill="#909090"/><line x1="0" y1="3" x2="7" y2="3" stroke="#606060" stroke-width="1"/><line x1="7" y1="7" x2="14" y2="7" stroke="#606060" stroke-width="1"/><line x1="3" y1="0" x2="3" y2="5" stroke="#606060" stroke-width="1"/><line x1="10" y1="5" x2="10" y2="10" stroke="#606060" stroke-width="1"/></pattern>
      <pattern id="p_default" patternUnits="userSpaceOnUse" width="8" height="8"><rect width="8" height="8" fill="#CCCCCC"/></pattern>
    </defs>
  `;

  const couchesSVG = couches.map((c, i) => {
    const style = NATURE_STYLES[c.nature] || NATURE_STYLES.default;
    const yTop = y(c.profondeur_toit), hCouche = h(c);
    return `
      <rect x="${MARGE_G}" y="${yTop}" width="${SVG_W - MARGE_G - MARGE_D}" height="${hCouche}"
        fill="url(#p_${c.nature in NATURE_STYLES ? c.nature : 'default'})" stroke="#555" stroke-width="0.8"/>
      <text x="${MARGE_G + 6}" y="${yTop + Math.min(hCouche * 0.45, 14)}" font-size="9.5" fill="#222" font-weight="600">${c.nom || style.label}</text>
      ${hCouche > 22 ? `<text x="${MARGE_G + 6}" y="${yTop + Math.min(hCouche * 0.75, 28)}" font-size="8.5" fill="#444">${c.nature} ${c.gamma ? `γ=${c.gamma}kN/m³` : ''}</text>` : ''}
    `;
  });

  const graduations = Array.from({ length: Math.ceil(profMax) + 1 }, (_, i) => {
    const yy = y(i);
    return `
      <line x1="${MARGE_G - 6}" y1="${yy}" x2="${MARGE_G}" y2="${yy}" stroke="#555" stroke-width="1"/>
      <text x="${MARGE_G - 8}" y="${yy + 4}" font-size="9" fill="#333" text-anchor="end">${i.toFixed(0)}</text>
    `;
  });

  const nappeEl = nappe && nappe_profondeur < profMax ? `
    <line x1="${MARGE_G}" y1="${y(nappe_profondeur)}" x2="${SVG_W - MARGE_D}" y2="${y(nappe_profondeur)}"
      stroke="#1a7bc4" stroke-width="1.8" stroke-dasharray="6,3"/>
    <text x="${SVG_W - MARGE_D - 2}" y="${y(nappe_profondeur) - 3}" font-size="9" fill="#1a7bc4" text-anchor="end" font-weight="bold">▼ NE=${nappe_profondeur}m</text>
  ` : '';

  return (
    <svg width={SVG_W} height={SVG_H} viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ background: "#fff", borderRadius: 8, border: "1px solid #e0e0e0" }}>
      <defs dangerouslySetInnerHTML={{ __html: patterns.replace(/<defs>|<\/defs>/g, "") }} />
      <text x={MARGE_G + (SVG_W - MARGE_G - MARGE_D) / 2} y="20" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#1a3a5c">SC-01 — Coupe lithostratigraphique</text>
      <text x={MARGE_G - 8} y="30" fontSize="9" fill="#666" textAnchor="end">Prof. (m)</text>
      <line x1={MARGE_G} y1={MARGE_H} x2={MARGE_G} y2={SVG_H - 10} stroke="#2c3e50" strokeWidth="1.5"/>
      <line x1={MARGE_G} y1={MARGE_H} x2={SVG_W - MARGE_D} y2={MARGE_H} stroke="#2c3e50" strokeWidth="1.5" strokeDasharray="4,3"/>
      <text x={SVG_W - MARGE_D} y={MARGE_H - 4} fontSize="9" fill="#444" textAnchor="end">TN</text>
      <g dangerouslySetInnerHTML={{ __html: couchesSVG.join("") }} />
      <g dangerouslySetInnerHTML={{ __html: graduations.join("") }} />
      <g dangerouslySetInnerHTML={{ __html: nappeEl }} />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════
// COMPOSANTS UI
// ═══════════════════════════════════════════════════════
const COULEURS_CAT = {
  fondations: "#2563eb", soutenement: "#d97706", stabilite: "#16a34a",
  hydraulique: "#0891b2", avance: "#7c3aed", geologie: "#92400e", essais: "#be185d", deformation: "#dc2626"
};

const CAT_LABELS = {
  fondations:"Fondations", soutenement:"Soutènement", stabilite:"Stabilité",
  hydraulique:"Hydraulique", avance:"Avancé (MEF)", geologie:"Géologie", essais:"Essais", deformation:"Déformation"
};


export default CoupeGeologique;
